import React from "react";

import { separateInputWrapperPropsAndInputProps } from "../../../../../organisms/Form/utils";

import { CheckboxGroup, CheckboxGroupProps } from "../../../../../molecules/CheckboxGroup";
import { usePyForm } from "../../../context";
import { FormField } from "../../FormComponents/FormField";
import { BaseFormFieldProps } from "../../FormComponents/FormField";

// Get all unique props from CheckboxGroup that aren't in BaseFormFieldProps
type UniqueInputProps<T> = Omit<CheckboxGroupProps<T>, keyof BaseFormFieldProps<T[]> | "value" | "onChange">;

export type FormCheckboxGroupProps<T> = Omit<BaseFormFieldProps<T[]>, "input"> & UniqueInputProps<T>;

/**
 * This is a form component that is used to create a checkbox group.
 * It is a wrapper around the CheckboxGroup component.
 * It is used to create a checkbox group that is connected to the PyForm context.
 * Its props cross-cut with the CheckboxGroup component and the FormField component.
 *
 * CheckboxGroup is a group of checkboxes and their labels
 */
export const FormCheckboxGroup = <T,>(props: FormCheckboxGroupProps<T>) => {
	const form = usePyForm();
	const { inputWrapperProps, inputProps } = separateInputWrapperPropsAndInputProps(props, form.id);

	return (
		<FormField<T[]>
			{...inputWrapperProps}
			input={(field) => (
				<CheckboxGroup
					{...inputProps}
					value={field.value}
					onChange={field.onChange}
					id={`${inputWrapperProps.id}`}
					data-testid={`${inputWrapperProps["data-testid"]}-checkbox-group`}
					disabled={inputWrapperProps.disabled}
					options={props.options}
				/>
			)}
		/>
	);
};

export default FormCheckboxGroup;
